import {
  DEFAULT_PAGE_WIDTH_PX,
  parseCssLengthToPx,
} from "../units";

/** Remove width locks so TipTap/prosemirror-tables column + row resize works. */
export function stripWidthFromCssStyle(style: string): string {
  return style
    .split(";")
    .map((part) => part.trim())
    .filter((part) => {
      if (!part) return false;
      const key = part.split(":")[0]?.trim().toLowerCase() ?? "";
      return !["width", "min-width", "max-width"].includes(key);
    })
    .join("; ")
    .trim();
}

/** Remove height locks from cell styles after row height is extracted. */
export function stripHeightFromCssStyle(style: string): string {
  return style
    .split(";")
    .map((part) => part.trim())
    .filter((part) => {
      if (!part) return false;
      const key = part.split(":")[0]?.trim().toLowerCase() ?? "";
      return !["height", "min-height", "max-height"].includes(key);
    })
    .join("; ")
    .trim();
}

function gridColumnIndex(
  cells: HTMLTableCellElement[],
  target: HTMLTableCellElement,
): number {
  let col = 0;
  for (const cell of cells) {
    if (cell === target) return col;
    col += cell.colSpan || 1;
  }
  return -1;
}

function readStylePropertyPx(
  style: string,
  prop: string,
  contextWidthPx?: number,
): number | null {
  const match = style.match(new RegExp(`\\b${prop}\\s*:\\s*([^;]+)`, "i"));
  if (!match) return null;
  return parseCssLengthToPx(match[1].trim(), contextWidthPx);
}

function readTableContextWidthPx(table: HTMLTableElement): number {
  const tableStyle = table.getAttribute("style") ?? "";
  const widthPx = readStylePropertyPx(tableStyle, "width");
  if (widthPx != null && widthPx > 0) return widthPx;
  return DEFAULT_PAGE_WIDTH_PX;
}

function readWidthsFromColwidthAttr(
  cells: HTMLTableCellElement[],
): number[] {
  let maxCol = 0;
  for (const cell of cells) {
    const colIndex = gridColumnIndex(cells, cell);
    if (colIndex < 0) continue;
    const attr = cell.getAttribute("colwidth");
    const span = attr ? attr.split(",").length : cell.colSpan || 1;
    maxCol = Math.max(maxCol, colIndex + span);
  }
  if (!maxCol) return [];

  const widths: number[] = Array(maxCol).fill(0);
  for (const cell of cells) {
    const colIndex = gridColumnIndex(cells, cell);
    if (colIndex < 0) continue;
    const attr = cell.getAttribute("colwidth");
    if (!attr) continue;
    const parts = attr.split(",").map((w) => parseInt(w.trim(), 10));
    if (parts.length > 1) {
      for (let i = 0; i < parts.length; i++) {
        const w = parts[i];
        if (w > 0) widths[colIndex + i] = w;
      }
    } else if (parts[0] > 0) {
      const colspan = cell.colSpan || 1;
      for (let i = 0; i < colspan; i++) {
        widths[colIndex + i] = parts[0];
      }
    }
  }
  return widths.some((w) => w > 0) ? widths : [];
}

function readWidthsFromColgroup(
  table: HTMLTableElement,
  contextWidthPx: number,
): number[] {
  const widths: number[] = [];
  table.querySelectorAll("colgroup > col").forEach((col) => {
    if (!(col instanceof HTMLElement)) return;
    const raw = col.style.width || col.getAttribute("width") || "";
    const px = parseCssLengthToPx(raw, contextWidthPx);
    widths.push(px != null && px > 0 ? px : 0);
  });
  return widths.some((w) => w > 0) ? widths : [];
}

function readWidthsFromCellStyles(
  cells: HTMLTableCellElement[],
  contextWidthPx: number,
): number[] {
  let maxCol = 0;
  for (const cell of cells) {
    const colIndex = gridColumnIndex(cells, cell);
    if (colIndex < 0) continue;
    maxCol = Math.max(maxCol, colIndex + (cell.colSpan || 1));
  }
  if (!maxCol) return [];

  const widths: number[] = Array(maxCol).fill(0);

  for (const cell of cells) {
    const colIndex = gridColumnIndex(cells, cell);
    if (colIndex < 0) continue;

    const style = cell.getAttribute("style") ?? "";
    const rawWidth =
      readStylePropertyPx(style, "width", contextWidthPx) ??
      readStylePropertyPx(style, "min-width", contextWidthPx);
    if (rawWidth == null || rawWidth <= 0) continue;

    const colspan = cell.colSpan || 1;
    if (colspan === 1) {
      widths[colIndex] = rawWidth;
    } else {
      const perCol = Math.round(rawWidth / colspan);
      for (let i = 0; i < colspan; i++) {
        if (!widths[colIndex + i]) widths[colIndex + i] = perCol;
      }
    }
  }

  return widths.some((w) => w > 0) ? widths : [];
}

function readWidthsFromAllRowCellStyles(
  table: HTMLTableElement,
  contextWidthPx: number,
): number[] {
  let best: number[] = [];
  table.querySelectorAll("tr").forEach((row) => {
    const cells = Array.from(row.children).filter(
      (node): node is HTMLTableCellElement => node instanceof HTMLTableCellElement,
    );
    const widths = readWidthsFromCellStyles(cells, contextWidthPx);
    if (widths.length > best.length) {
      best = widths;
    } else if (
      widths.length === best.length &&
      widths.filter((w) => w > 0).length > best.filter((w) => w > 0).length
    ) {
      best = widths;
    }
  });
  return best;
}

/**
 * Resolve column widths (px) from colwidth attrs, colgroup, or first-row cell widths.
 */
export function resolveColumnWidthsFromHtml(table: HTMLTableElement): number[] {
  const contextWidthPx = readTableContextWidthPx(table);
  const firstRow = table.querySelector("tr");
  if (!firstRow) return [];

  const cells = Array.from(firstRow.children).filter(
    (node): node is HTMLTableCellElement => node instanceof HTMLTableCellElement,
  );

  const fromColwidth = readWidthsFromColwidthAttr(cells);
  if (fromColwidth.length > 0) return fromColwidth;

  const fromColgroup = readWidthsFromColgroup(table, contextWidthPx);
  if (fromColgroup.length > 0) return fromColgroup;

  return readWidthsFromAllRowCellStyles(table, contextWidthPx);
}

function preserveTableWidth(table: HTMLTableElement): void {
  const style = table.getAttribute("style") ?? "";
  const match = style.match(/\bwidth\s*:\s*([^;]+)/i);
  if (!match) return;

  const raw = match[1].trim();
  if (/%$/i.test(raw)) return;

  const px = parseCssLengthToPx(raw);
  if (px != null && px > 0) {
    table.style.width = `${px}px`;
  }
}

function applyColwidthToRow(
  row: HTMLTableRowElement,
  columnWidths: number[],
): void {
  const cells = Array.from(row.children).filter(
    (node): node is HTMLTableCellElement => node instanceof HTMLTableCellElement,
  );

  for (const cell of cells) {
    const colIndex = gridColumnIndex(cells, cell);
    if (colIndex < 0) continue;

    const colspan = cell.colSpan || 1;
    const slice = columnWidths.slice(colIndex, colIndex + colspan);
    if (slice.length !== colspan || !slice.every((w) => w > 0)) continue;

    cell.setAttribute("colwidth", slice.join(","));
  }
}

function readStyleHeightPx(style: string): number | null {
  return (
    readStylePropertyPx(style, "height") ??
    readStylePropertyPx(style, "min-height")
  );
}

const VALID_TEXT_ALIGNS = new Set(["left", "center", "right", "justify"]);

function normalizeTextAlignValue(
  raw: string | null | undefined,
): string | null {
  if (!raw) return null;
  const value = raw.trim().toLowerCase();
  if (value === "start") return "left";
  if (value === "end") return "right";
  return VALID_TEXT_ALIGNS.has(value) ? value : null;
}

function normalizeVerticalAlignValue(
  raw: string | null | undefined,
): string | null {
  if (!raw) return null;
  const value = raw.trim().toLowerCase();
  if (value === "center" || value === "middle") return "middle";
  if (value === "top" || value === "bottom") return value;
  return null;
}

function alignmentFromClass(className: string): string | null {
  const classes = className.toLowerCase();
  if (/\bcenter\b/.test(classes) || classes.includes("text-center")) {
    return "center";
  }
  if (/\bright\b/.test(classes) || classes.includes("text-right")) {
    return "right";
  }
  if (/\bjustify\b/.test(classes) || classes.includes("text-justify")) {
    return "justify";
  }
  if (/\bleft\b/.test(classes) || classes.includes("text-left")) {
    return "left";
  }
  return null;
}

function readBlockTextAlign(block: HTMLElement): string | null {
  return (
    normalizeTextAlignValue(block.style.textAlign) ??
    normalizeTextAlignValue(block.getAttribute("align")) ??
    alignmentFromClass(block.className || "")
  );
}

function readCellTextAlign(cell: HTMLTableCellElement): string | null {
  const fromStyle = normalizeTextAlignValue(cell.style.textAlign);
  if (fromStyle) return fromStyle;

  const fromAttr = normalizeTextAlignValue(cell.getAttribute("align"));
  if (fromAttr) return fromAttr;

  const fromClass = alignmentFromClass(cell.className || "");
  if (fromClass) return fromClass;

  const blocks = cell.querySelectorAll(
    ":scope > p, :scope > h1, :scope > h2, :scope > h3, :scope > h4, :scope > h5, :scope > h6, :scope > li",
  );
  for (const block of blocks) {
    if (!(block instanceof HTMLElement)) continue;
    const fromBlock = readBlockTextAlign(block);
    if (fromBlock) return fromBlock;

    const span = block.querySelector(":scope > span[style], :scope > span[align]");
    if (span instanceof HTMLElement) {
      const fromSpan = readBlockTextAlign(span);
      if (fromSpan) return fromSpan;
    }
  }

  return null;
}

function readCellVerticalAlign(cell: HTMLTableCellElement): string | null {
  const fromStyle = normalizeVerticalAlignValue(cell.style.verticalAlign);
  if (fromStyle) return fromStyle;

  return normalizeVerticalAlignValue(cell.getAttribute("valign"));
}

/** Preserve Word cell alignment on td/th and propagate to inner block elements. */
export function normalizeTableCellAlignments(table: HTMLTableElement): void {
  table.querySelectorAll("td, th").forEach((cell) => {
    if (!(cell instanceof HTMLTableCellElement)) return;

    const textAlign = readCellTextAlign(cell);
    if (textAlign) {
      cell.style.textAlign = textAlign;
      const blocks = cell.querySelectorAll(
        ":scope > p, :scope > h1, :scope > h2, :scope > h3, :scope > h4, :scope > h5, :scope > h6, :scope > li",
      );
      if (blocks.length > 0) {
        blocks.forEach((block) => {
          if (!(block instanceof HTMLElement)) return;
          if (!readBlockTextAlign(block)) {
            block.style.textAlign = textAlign;
          }
        });
      } else if (cell.textContent?.trim()) {
        const p = cell.ownerDocument.createElement("p");
        p.style.textAlign = textAlign;
        while (cell.firstChild) {
          p.appendChild(cell.firstChild);
        }
        cell.appendChild(p);
      }
    }

    const verticalAlign = readCellVerticalAlign(cell);
    if (verticalAlign) {
      cell.style.verticalAlign = verticalAlign;
    }

    cell.removeAttribute("align");
    cell.removeAttribute("valign");
  });
}

function extractRowHeights(table: HTMLTableElement): void {
  table.querySelectorAll("tr").forEach((row) => {
    if (!(row instanceof HTMLTableRowElement)) return;

    let heightPx = readStyleHeightPx(row.getAttribute("style") ?? "");

    if (heightPx == null) {
      row.querySelectorAll("td, th").forEach((cell) => {
        if (!(cell instanceof HTMLElement)) return;
        const cellHeight = readStyleHeightPx(cell.getAttribute("style") ?? "");
        if (cellHeight != null && cellHeight > 0) {
          heightPx =
            heightPx == null ? cellHeight : Math.max(heightPx, cellHeight);
        }
      });
    }

    if (heightPx != null && heightPx > 0) {
      row.style.height = `${heightPx}px`;
    }
  });
}

/** Re-apply per-cell alignment after other import heuristics run. */
export function refreshTableCellAlignments(root: ParentNode): void {
  root.querySelectorAll("table").forEach((table) => {
    if (table instanceof HTMLTableElement) {
      normalizeTableCellAlignments(table);
    }
  });
}

/**
 * Strip import-only locks (colgroup, inline cell widths) and seed colwidth on every row.
 */
export function prepareTableForEditorResize(table: HTMLTableElement): void {
  normalizeTableCellAlignments(table);

  const columnWidths = resolveColumnWidthsFromHtml(table);

  preserveTableWidth(table);

  if (columnWidths.some((w) => w > 0)) {
    table.style.tableLayout = "fixed";
  }

  extractRowHeights(table);

  table.querySelectorAll("td, th").forEach((cell) => {
    if (!(cell instanceof HTMLElement)) return;

    const cleanedWidth = stripWidthFromCssStyle(cell.getAttribute("style") ?? "");
    const cleaned = stripHeightFromCssStyle(cleanedWidth);
    if (cleaned) cell.setAttribute("style", cleaned);
    else cell.removeAttribute("style");

    cell.style.height = "";
  });

  table.querySelectorAll("colgroup").forEach((cg) => cg.remove());

  if (columnWidths.length > 0) {
    table.querySelectorAll("tr").forEach((row) => {
      if (row instanceof HTMLTableRowElement) {
        applyColwidthToRow(row, columnWidths);
      }
    });
  }

  if (table.style.textAlign) {
    table.style.textAlign = "";
  }
}
